import React from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import authService from '../services/authService';
import {
  AuthAlert,
  AuthField,
  AuthInput,
  AuthPageLayout,
  AuthSubmitButton,
  authStyles,
} from './AuthPageLayout';

const RESET_CODE_LENGTH = 6;

const normalizeResetCode = (value: string) => value.replace(/\D/g, '').slice(0, RESET_CODE_LENGTH);

export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const requiresPasswordChange = searchParams.get('required') === '1';
  const codeSent = Boolean((location.state as { codeSent?: boolean } | null)?.codeSent);
  const codeSentMessage = (location.state as { message?: string } | null)?.message || null;
  const usernameFromState = (location.state as { username?: string } | null)?.username || '';
  const usernameFromQuery = searchParams.get('username') || '';
  const username = (usernameFromState || usernameFromQuery).trim();
  const codeFromQuery = searchParams.get('code') || searchParams.get('token') || '';
  const tenantIdFromState = (location.state as { tenantId?: string } | null)?.tenantId || '';
  const tenantIdFromQuery = searchParams.get('tenantId') || '';
  const tenantId = (tenantIdFromState || tenantIdFromQuery).trim();
  const tenantUrlFromState = (location.state as { tenantUrl?: string } | null)?.tenantUrl || '';
  const tenantUrlFromQuery = searchParams.get('tenantUrl') || '';
  const tenantUrl = (tenantUrlFromState || tenantUrlFromQuery).trim();

  const [resetCode, setResetCode] = React.useState(() => normalizeResetCode(codeFromQuery));
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showCurrentPassword, setShowCurrentPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(codeSentMessage);

  React.useEffect(() => {
    if (!codeFromQuery) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('code');
    nextParams.delete('token');
    setSearchParams(nextParams, { replace: true });
  }, [codeFromQuery, searchParams, setSearchParams]);

  const resetSubtitle = requiresPasswordChange
    ? t('firstLoginSubtitle')
    : codeSent
      ? t('resetPasswordCodeSentSubtitle')
      : t('resetPasswordSubtitle');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (requiresPasswordChange) {
      if (!username) {
        setError(t('errors.firstLoginUsernameMissing'));
        return;
      }

      if (!currentPassword) {
        setError(t('errors.currentPasswordRequired'));
        return;
      }
    } else if (!resetCode.trim()) {
      setError(t('errors.resetTokenRequired'));
      return;
    } else if (resetCode.length !== RESET_CODE_LENGTH) {
      setError(t('errors.resetCodeInvalid'));
      return;
    }

    if (!newPassword) {
      setError(t('errors.newPasswordRequired'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('errors.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      const result = requiresPasswordChange
        ? await authService.firstLoginPasswordChange({
          username,
          currentPassword,
          newPassword,
          tenantId,
          tenantUrl,
        })
        : await authService.resetPassword(resetCode.trim(), newPassword, { tenantId, tenantUrl }, username);
      setMessage(result.message);
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unableToResetPassword'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout
      title={t('resetPasswordTitle')}
      subtitle={resetSubtitle}
    >
      <form className={authStyles.form} onSubmit={onSubmit}>
        {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}
        {message ? <AuthAlert variant="success">{message}</AuthAlert> : null}

        {requiresPasswordChange && (
          <AuthField label={t('currentPasswordLabel')} htmlFor="reset-current-password" icon="lock">
            <AuthInput
              id="reset-current-password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              withToggle
              revealed={showCurrentPassword}
              onToggleReveal={() => setShowCurrentPassword(current => !current)}
              toggleLabel={showCurrentPassword ? t('hidePassword') : t('showPassword')}
            />
          </AuthField>
        )}

        {!requiresPasswordChange && (
          <AuthField label={t('resetTokenLabel')} htmlFor="reset-code">
            <AuthInput
              id="reset-code"
              variant="code"
              value={resetCode}
              onChange={e => setResetCode(normalizeResetCode(e.target.value))}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={RESET_CODE_LENGTH}
              placeholder={t('resetCodePlaceholder')}
            />
          </AuthField>
        )}

        <AuthField label={t('newPasswordLabel')} htmlFor="reset-new-password" icon="lock">
          <AuthInput
            id="reset-new-password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
            withToggle
            revealed={showNewPassword}
            onToggleReveal={() => setShowNewPassword(current => !current)}
            toggleLabel={showNewPassword ? t('hidePassword') : t('showPassword')}
          />
        </AuthField>

        <AuthField label={t('confirmPasswordLabel')} htmlFor="reset-confirm-password" icon="lock">
          <AuthInput
            id="reset-confirm-password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            withToggle
            revealed={showConfirmPassword}
            onToggleReveal={() => setShowConfirmPassword(current => !current)}
            toggleLabel={showConfirmPassword ? t('hidePassword') : t('showPassword')}
          />
        </AuthField>

        <AuthSubmitButton loading={loading} loadingLabel={t('sending')}>
          {t('resetPasswordAction')}
        </AuthSubmitButton>

        <div className={authStyles.footerLinks}>
          {!requiresPasswordChange && (
            <Link className={authStyles.link} to="/forgot-password">
              {t('requestNewToken')}
            </Link>
          )}
          <Link className={authStyles.link} to="/login">
            {t('backToLogin')}
          </Link>
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default ResetPasswordPage;
